import { TERMS_BUNDLE_NAMESPACES, type FlatBundle } from '@/legal/termsBundle'

/**
 * Renders an archived version from its flat strings, so a version stays
 * readable after the live pages moved on. The structure comes from the key
 * paths alone: the archive holds no markup.
 */

export interface DocHeading {
  kind: 'heading'
  key: string
  level: number
  label?: string
  text: string
}

export interface DocText {
  kind: 'paragraph' | 'item'
  key: string
  label?: string
  lead?: string
  text: string
}

export type DocBlock = DocHeading | DocText

export interface TermsDocument {
  namespace: string
  title: string
  intro: string[]
  blocks: DocBlock[]
}

const SKIPPED_GROUPS = new Set(['toc', 'partDividers', 'acceptance'])
// The only agreement section the AGB page numbers by paragraph, besides the general terms.
const NUMBERED_SECTIONS = new Set(['termsPage.domains.section6'])
const SKIPPED_LEAVES = new Set(['version', 'backToHome', 'ariaLabel'])
const INTRO_LEAVES = new Set(['subtitle', 'lastUpdated'])

const lastSegment = (key: string) => key.slice(key.lastIndexOf('.') + 1)
const parentOf = (key: string) => key.slice(0, key.lastIndexOf('.'))

/** Keys that are text of the document, as opposed to page furniture. */
export const isContentKey = (key: string): boolean => {
  const parts = key.split('.')
  if (parts.length < 2) return false
  if (SKIPPED_GROUPS.has(parts[1])) return false
  if (parts.length === 2 && (parts[1] === 'title' || INTRO_LEAVES.has(parts[1]))) return false
  return !SKIPPED_LEAVES.has(parts[parts.length - 1])
}

/** The deepest enclosing path that carries a title: the heading a key reads under. */
export const sectionOf = (key: string, flat: FlatBundle): string => {
  let path = parentOf(key)
  while (path.includes('.')) {
    if (`${path}.title` in flat) return path
    path = parentOf(path)
  }
  return path
}

/** Values an interpolation names that the archive cannot hold, such as the status page address. */
export type DocParams = Record<string, string>

/** Formats archived message source: literal escapes and named interpolations, nothing else. */
export const interpolateMessage = (raw: string, resolve: (name: string) => string | undefined): string =>
  raw.replace(/\{(?:'([^']*)'|(\w+))\}/g, (match, literal: string | undefined, name: string | undefined) =>
    literal !== undefined ? literal : (resolve(name!) ?? match)
  )

// Named slots the live pages fill with a sibling string ({privacyLink}, {notBold} from `not`) or from config.
const resolveText = (key: string, flat: FlatBundle, params: DocParams, consumed: Set<string>): string => {
  const parent = parentOf(key)
  return interpolateMessage(flat[key], (name) => {
    for (const sibling of [name, name.replace(/Bold$/, '')]) {
      const siblingKey = `${parent}.${sibling}`
      if (siblingKey !== key && siblingKey in flat) {
        consumed.add(siblingKey)
        return interpolateMessage(flat[siblingKey], () => undefined)
      }
    }
    return params[name]
  })
}

const sectionNumber = (path: string): string | undefined => /section(\d+)$/.exec(path)?.[1]

export const renderDocument = (flat: FlatBundle, namespace: string, params: DocParams = {}): TermsDocument => {
  const keys = Object.keys(flat).filter((k) => k.startsWith(`${namespace}.`))
  const consumed = new Set<string>()
  const texts = new Map<string, string>()
  for (const key of keys) texts.set(key, resolveText(key, flat, params, consumed))

  const doc: TermsDocument = {
    namespace,
    title: texts.get(`${namespace}.title`) ?? namespace,
    intro: [...INTRO_LEAVES].map((leaf) => texts.get(`${namespace}.${leaf}`)).filter((t): t is string => !!t),
    blocks: []
  }

  // An xTitle leads its x, a label its sibling text.
  const leadTarget = (key: string): string | undefined => {
    const leaf = lastSegment(key)
    const sibling = /^(\w+)Title$/.exec(leaf)?.[1] ?? (leaf === 'label' ? 'text' : undefined)
    if (!sibling) return undefined
    const target = `${parentOf(key)}.${sibling}`
    return target in flat ? target : undefined
  }
  const leads = new Map<string, string>()
  for (const key of keys) {
    const target = leadTarget(key)
    if (target) leads.set(target, texts.get(key)!)
  }

  const numberOf = (section: string) => (namespace === 'termsPage' ? sectionNumber(section) : undefined)
  const isTopLevel = (section: string, number: string) => section === `${namespace}.section${number}`
  const blockKeys = keys.filter((key) => isContentKey(key) && !consumed.has(key) && !leadTarget(key))

  // Numbered by place, as the AGB page counts: a removed pN leaves no gap, and
  // a titled paragraph (the § 12 waiver) takes a number of its own.
  const paragraphNumbers = new Map<string, number>()
  const counts = new Map<string, number>()
  for (const key of blockKeys) {
    if (!/^p\d+$/.test(lastSegment(key)) && !leads.has(key)) continue
    const section = parentOf(key)
    const number = numberOf(section)
    const numbered = number && (isTopLevel(section, number) || NUMBERED_SECTIONS.has(section))
    if (!numbered || sectionOf(key, flat) !== section) continue
    const n = (counts.get(section) ?? 0) + 1
    counts.set(section, n)
    paragraphNumbers.set(key, n)
  }

  for (const key of blockKeys) {
    const leaf = lastSegment(key)
    const text = texts.get(key)!

    if (leaf === 'title') {
      const section = parentOf(key)
      const number = numberOf(section)
      let label: string | undefined
      if (number) label = isTopLevel(section, number) ? `§ ${number}` : number
      doc.blocks.push({ kind: 'heading', key, level: Math.min(key.split('.').length - 1, 4), label, text })
      continue
    }

    const section = sectionOf(key, flat)
    const paragraph = paragraphNumbers.get(key)
    doc.blocks.push({
      kind: parentOf(key) === section ? 'paragraph' : 'item',
      key,
      label: paragraph ? `${numberOf(section)}.${paragraph}` : undefined,
      lead: leads.get(key),
      text
    })
  }
  return doc
}

export const renderBundle = (flat: FlatBundle, params: DocParams = {}): TermsDocument[] =>
  TERMS_BUNDLE_NAMESPACES.map((ns) => renderDocument(flat, ns, params)).filter((d) => d.blocks.length > 0)

export interface TermsChange {
  key: string
  kind: 'changed' | 'added' | 'removed'
  before?: string
  after?: string
}

export interface TermsChangeGroup {
  document: string
  section: string
  heading: string
  changes: TermsChange[]
}

const fullText = (block: DocBlock | undefined): string | undefined => {
  if (block === undefined) return undefined
  return block.kind !== 'heading' && block.lead ? `${block.lead}\n${block.text}` : block.text
}

const changeKind = (was: string | undefined, now: string | undefined): TermsChange['kind'] => {
  if (was === undefined) return 'added'
  if (now === undefined) return 'removed'
  return 'changed'
}

/**
 * Paragraph by paragraph changes from one version to the next, grouped under
 * the heading each paragraph reads under. Rendered text is compared, so a
 * changed interpolated sibling (a link label) shows on the paragraph using it.
 */
export const compareBundles = (before: FlatBundle, after: FlatBundle, params: DocParams = {}): TermsChangeGroup[] => {
  const groups: TermsChangeGroup[] = []
  for (const ns of TERMS_BUNDLE_NAMESPACES) {
    const oldDoc = renderDocument(before, ns, params)
    const newDoc = renderDocument(after, ns, params)
    const oldTexts = new Map(oldDoc.blocks.map((b) => [b.key, b] as const))
    const newTexts = new Map(newDoc.blocks.map((b) => [b.key, b] as const))

    const byGroup = new Map<string, TermsChangeGroup>()
    const keys = [...newTexts.keys(), ...[...oldTexts.keys()].filter((k) => !newTexts.has(k))]
    for (const key of keys) {
      const was = fullText(oldTexts.get(key))
      const now = fullText(newTexts.get(key))
      if (was === now) continue
      const inNew = newTexts.has(key)
      const flat = inNew ? after : before
      const section = lastSegment(key) === 'title' ? parentOf(key) : sectionOf(key, flat)
      let group = byGroup.get(section)
      if (!group) {
        const headingKey = `${section}.title`
        group = {
          document: (inNew ? newDoc : oldDoc).title,
          section,
          heading: newTexts.get(headingKey)?.text ?? oldTexts.get(headingKey)?.text ?? flat[headingKey] ?? '',
          changes: []
        }
        byGroup.set(section, group)
        groups.push(group)
      }
      group.changes.push({ key, kind: changeKind(was, now), before: was, after: now })
    }
  }
  return groups
}
